<div class="zag-collection-layout">
  <!-- ── Left Sidebar ────────────────────────────────────────────────────── -->
  <aside class="zag-collection-layout__sidebar">
    <!-- Profile Card: icon + title + description -->
    @if (hasProfile()) {
      <div class="zag-cl-profile-card">
        <div class="zag-cl-profile-card__icon-wrapper" [class.zag-cl-profile-card__icon-wrapper--fallback]="!config().icon">
          <span [zagIcon]="config().icon || 'folder'" class="zag-cl-profile-card__icon"></span>
        </div>
        <div class="zag-cl-profile-card__info">
          @if (config().title) {
            <h2 class="zag-cl-profile-card__title">{{ config().title | translate }}</h2>
          }
          @if (config().badge; as badge) {
            <zag-badge class="zag-cl-profile-card__badge" [color]="badge.color ?? 'info'" [accent]="true">
              @if (badge.icon) {
                <span [zagIcon]="badge.icon" class="zag-cl-profile-card__badge-icon" aria-hidden="true"></span>
              }
              {{ badge.label | translate }}
            </zag-badge>
          }
          @if (config().description) {
            <p class="zag-cl-profile-card__description">{{ config().description | translate }}</p>
          }
        </div>

        @if (showToolbarInProfile()) {
          <div class="zag-cl-profile-card__extra">
            <ng-container *ngTemplateOutlet="toolbarContent()!" />
          </div>
        }
      </div>
    }

    <!-- Stats Card: total + filtered counts — always show both -->
    @if (hasProfile() && showStats()) {
      <div class="zag-cl-stats-card">
        <!-- TOTAL stat: unfilteredTotal when provided, otherwise same as filtered -->
        <div class="zag-cl-stats-card__stat">
          <div class="zag-cl-stats-card__icon-wrapper">
            <span zagIcon="list" class="zag-cl-stats-card__icon"></span>
          </div>
          <div class="zag-cl-stats-card__content">
            <span class="zag-cl-stats-card__value">
              {{ unfilteredTotal() !== undefined ? unfilteredTotal() : totalCount() !== undefined ? totalCount() : items().length }}
            </span>
            <span class="zag-cl-stats-card__label">{{ 'common.total' | translate }}</span>
          </div>
        </div>
        <!-- FILTERED stat: always shown alongside TOTAL -->
        <div class="zag-cl-stats-card__stat">
          <div class="zag-cl-stats-card__icon-wrapper">
            <span zagIcon="filter-alt" class="zag-cl-stats-card__icon"></span>
          </div>
          <div class="zag-cl-stats-card__content">
            <span class="zag-cl-stats-card__value">{{ totalCount() !== undefined ? totalCount() : items().length }}</span>
            <span class="zag-cl-stats-card__label">{{ 'common.filtered' | translate }}</span>
          </div>
        </div>
        <!-- Domain KPIs contributed by the consumer -->
        @for (stat of stats(); track stat.id) {
          <div class="zag-cl-stats-card__stat">
            <div class="zag-cl-stats-card__icon-wrapper">
              <span [zagIcon]="stat.icon" class="zag-cl-stats-card__icon"></span>
            </div>
            <div class="zag-cl-stats-card__content">
              <span class="zag-cl-stats-card__value">{{ stat.value }}</span>
              <span class="zag-cl-stats-card__label">{{ stat.label | translate }}</span>
            </div>
          </div>
        }
      </div>
    }

    <!-- View mode + sort controls — each rendered as its own card by controls component -->
    @if (hasControls()) {
      <zag-collection-layout-controls
        [viewModes]="config().viewModes"
        [sortOptions]="config().sortOptions"
        [filterGroups]="resolvedFilterGroups()"
        [(viewMode)]="viewMode"
        [(sortBy)]="sortBy"
        [(sortDirection)]="sortDirection"
        [filterValues]="resolvedFilterValues()"
        (filterValuesChange)="handleFilterValuesChange($event)"
      />
    }

    <!-- Additional toolbar content (e.g. custom filters) — own card -->
    @if (showToolbarCard()) {
      <div class="zag-collection-layout__sidebar-card">
        <ng-container *ngTemplateOutlet="toolbarContent()!"></ng-container>
      </div>
    }

    <!-- Selection controls stay available alongside projected actions. -->
    @if (selectionEnabled() && selectionMode()) {
      <div class="zag-cl-selection-card" [class.zag-cl-selection-card--projected]="!!selectionActions()">
        @if (!selectionActions()) {
          <div class="zag-cl-selection-card__summary">
            <span class="zag-cl-selection-card__count">{{ selectionCount() }}</span>
            <span class="zag-cl-selection-card__label">
              {{ 'common.selectedCount' | translate: { selected: selectionCount(), total: matchingSelectionTotal() } }}
            </span>
          </div>
        }
        <button
          zagButton="bare"
          class="zag-collection-layout__action-btn"
          type="button"
          [class.zag-collection-layout__action-btn--active]="selectionAllMatchingActive()"
          [attr.aria-pressed]="selectionAllMatchingActive()"
          (click)="onSelectAllMatching(!selectionAllMatchingActive())"
        >
          <span [zagIcon]="selectionAllMatchingActive() ? 'check-box' : 'check-box-outline-blank'" aria-hidden="true"></span>
          {{ 'common.selectAllMatching' | translate }}
        </button>
        @if (selectionActions(); as actions) {
          <ng-container *ngTemplateOutlet="actions; context: { $implicit: selectionContext(), ...selectionContext() }" />
        } @else {
          <div class="zag-cl-selection-card__actions">
            <button zagButton="bare" class="zag-collection-layout__action-btn" type="button" (click)="exitSelectionMode()">
              <span zagIcon="close" aria-hidden="true"></span>
              {{ 'common.cancelSelection' | translate }}
            </button>
          </div>
        }
      </div>
    }

    <!-- Primary action card (e.g. "Add new" button) — at the bottom -->
    @if (headerContent() && showHeaderActions()) {
      <div class="zag-collection-layout__sidebar-card zag-collection-layout__sidebar-card--actions">
        <div class="zag-collection-layout__actions-header">
          <span class="zag-collection-layout__actions-title">{{ 'common.quickActions' | translate }}</span>
        </div>
        <div
          class="zag-collection-layout__actions-body"
          [class.zag-collection-layout__actions-body--selection-active]="selectionMode()"
          [attr.inert]="selectionMode() ? '' : null"
          [attr.aria-disabled]="selectionMode() ? 'true' : null"
        >
          @if (selectionAvailable() && !selectionMode()) {
            <button zagButton="bare" class="zag-collection-layout__action-btn" type="button" (click)="enterSelectionMode()">
              <span zagIcon="edit"></span>
              {{ 'common.selectionMode' | translate }}
            </button>
          }
          <ng-container *ngTemplateOutlet="headerContent()!"></ng-container>
        </div>
      </div>
    }

    @if (resourcePath() && collectionKey() && bulkEditAvailable()) {
      <div class="zag-collection-layout__sidebar-card zag-collection-layout__sidebar-card--actions">
        <zag-recent-bulk-edits-card [collectionKey]="collectionKey()" />
      </div>
    }
  </aside>

  <!-- ── Right Content ───────────────────────────────────────────────────── -->
  <main class="zag-collection-layout__main">
    <!-- Content States -->
    @if (isInitialLoad()) {
      <!-- Initial Loading State (first load only — no prior data) -->
      <div class="zag-collection-layout__state zag-collection-layout__state--loading">
        <span zagIcon="sync" class="zag-collection-layout__spinner"></span>
        <p>{{ 'common.loading' | translate }}</p>
      </div>
    } @else if (error()) {
      <!-- Error State -->
      <div class="zag-collection-layout__state zag-collection-layout__state--error">
        <span zagIcon="error-outline"></span>
        <p>{{ 'common.error' | translate }}</p>
        <button zagButton type="button" (click)="retry.emit()">
          {{ 'common.retry' | translate }}
        </button>
      </div>
    } @else if (showEmpty()) {
      <!-- Empty State -->
      <div class="zag-collection-layout__state zag-collection-layout__state--empty">
        @if (emptyContent()) {
          <ng-container *ngTemplateOutlet="emptyContent()!"></ng-container>
        } @else {
          <span zagIcon="inbox"></span>
          <p>{{ 'common.noResults' | translate }}</p>
        }
      </div>
    } @else {
      <!-- Reload indicator: subtle spinner shown when reloading with prior data -->
      @if (loading()) {
        <div class="zag-collection-layout__reload-indicator">
          <span zagIcon="sync" class="zag-collection-layout__spinner"></span>
        </div>
      }
      <!-- Scrollable Content -->
      <div
        #scrollContainer
        class="zag-collection-layout__content"
        [class.zag-collection-layout__content--grid]="isCardMode()"
        [class.zag-collection-layout__content--list]="!isCardMode()"
        [class.zag-collection-layout__content--reloading]="loading()"
        (scroll)="onScroll($event)"
      >
        @for (item of visibleItems(); track trackBy()(item)) {
          @if (selectionEnabled() && selectionMode()) {
            <div
              [zagSelectable]="getEntityId(item)"
              [selectionMode]="selectionMode() && isSelectable(item)"
              class="zag-collection-layout__selectable-wrapper"
            >
              @if (isCardMode() && cardTemplate()) {
                <ng-container *ngTemplateOutlet="cardTemplate()!; context: { $implicit: item }"> </ng-container>
              } @else if (!isCardMode() && listTemplate()) {
                <ng-container *ngTemplateOutlet="listTemplate()!; context: { $implicit: item }"> </ng-container>
              }
            </div>
          } @else {
            @if (isCardMode() && cardTemplate()) {
              <ng-container *ngTemplateOutlet="cardTemplate()!; context: { $implicit: item }"> </ng-container>
            } @else if (!isCardMode() && listTemplate()) {
              <ng-container *ngTemplateOutlet="listTemplate()!; context: { $implicit: item }"> </ng-container>
            }
          }
        }

        <!-- Load More Indicator -->
        @if (hasMore()) {
          <div class="zag-collection-layout__load-more">
            @if (loadingMore()) {
              <span zagIcon="sync" class="zag-collection-layout__spinner"></span>
            }
          </div>
        }
      </div>
    }
  </main>
</div>
